import React from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "firebaseClient";
import { Box, Typography, Card, CardContent } from "@mui/material";

import themes from "config/themes";
import { useGlobalConfig } from "providers/GlobalConfig";
import { GlobalConfigType, UserConfig } from "types/configs";
import { updateUserConfig } from "models/configs";
import { useSelfServeAlert } from "pages/SelfServe/utils/useSelfServeAlert";

import { useSurveyForm } from "./hooks/useSurveyForm";
import {
  REFERRAL_SOURCE_OPTIONS,
  ONBOARDING_STEPS,
  SELF_SERVE_TENANT_ID,
  ENROLL_PATH,
} from "types/onboarding";

import Stepper from "../../sharedComponents/Stepper";
import { FormSection } from "./components/FormSection";
import {
  CompanyNameQuestion,
  GoalsQuestion,
  SelectQuestion,
} from "./components/Questions";
import { SaveAndContinueButton } from "pages/SelfServe/sharedComponents/SaveAndContinueButton";

const Survey: React.FC = () => {
  const navigate = useNavigate();
  const { globalConfig } = useGlobalConfig()! as GlobalConfigType;
  const { formData, errors, updateField, validateForm, showError } =
    useSurveyForm();
  const { sendAlert } = useSelfServeAlert();
  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstKey = Object.keys(errors)[0];
      const anchor = document.getElementById(`q-${firstKey}`);
      anchor?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const tenantName = formData.companyName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();

    const updatedConfig: Partial<UserConfig> = {
      tenant: SELF_SERVE_TENANT_ID,
      props: {
        disabledAnnouncements: [],
        isCasesExpanded: false,
        onboarding: {
          currentStep: ONBOARDING_STEPS.catalog,
          survey: {
            ...formData,
            tenant: tenantName,
          },
          productCatalog: [],
        },
      },
    };

    try {
      await sendAlert({
        customerName: formData.companyName,
        step: ONBOARDING_STEPS.survey,
        survey: {
          ...formData,
        },
      });
    } catch (error) {
      console.error("Error sending self-serve alert:", error);
    }

    await updateUserConfig(auth, globalConfig, updatedConfig);
    navigate(`${ENROLL_PATH}/${ONBOARDING_STEPS.catalog}`);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Stepper currentStep={1} />

      <Box
        sx={{
          flex: 1,
          padding: { xs: 3, md: "0px 48px" },
          backgroundColor: themes.palette.neutral.main,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Card
          sx={{
            maxWidth: 700,
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: "24px", md: "32px" },
              }}
            >
              Welcome Aboard!
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
              Help us better serve you by answering a few quick questions
            </Typography>

            <FormSection
              id="q-companyName"
              questionNumber={1}
              question="Enter your company name."
            >
              <CompanyNameQuestion
                value={formData.companyName}
                onChange={(val) => updateField("companyName", val)}
                error={errors.companyName}
                showError={showError("companyName")}
              />
            </FormSection>

            <FormSection
              id="q-goals"
              questionNumber={2}
              question="What are you hoping to achieve with our platform? (Select at least one)"
            >
              <GoalsQuestion
                selectedGoals={formData.goals}
                onChange={(goals) => updateField("goals", goals)}
                error={errors.goals}
                showError={showError("goals")}
              />
            </FormSection>

            <FormSection
              id="q-referralSource"
              questionNumber={3}
              question="How did you hear about us?"
            >
              <SelectQuestion
                label="Select a source"
                value={formData.referralSource}
                options={REFERRAL_SOURCE_OPTIONS}
                onChange={(val) => updateField("referralSource", val)}
                error={errors.referralSource}
                showError={showError("referralSource")}
              />
            </FormSection>

            <SaveAndContinueButton onConfirm={handleSubmit} disabled={false} />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Survey;
